import { useEffect, useState, useMemo } from "react";
import { useFormikContext } from "formik";
import PharmacyFinder from "./PharmacySearch";
import './Pharmacy.css';

const Pharmacy = ({ moduleData, extractedPharmacyDetails = [] }) => {
  const { setFieldValue, values } = useFormikContext();
  const [blobUrls, setBlobUrls] = useState({});
  const [pharmacyData, setPharmacyData] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [hasShownPopup, setHasShownPopup] = useState(false);

  // ✅ Helper function to validate pharmacy data is not empty/blank
  const isValidPharmacy = (pharmacy) => {
    if (!pharmacy) return false;
    // A pharmacy is valid if it has at least a non-empty name
    const hasValidName = pharmacy.name && typeof pharmacy.name === 'string' && pharmacy.name.trim() !== '';
    return hasValidName;
  };

  // ✅ Filter out invalid/blank pharmacy entries
  const validPharmacyDetails = useMemo(() => {
    if (!Array.isArray(extractedPharmacyDetails)) return [];
    return extractedPharmacyDetails.filter(isValidPharmacy);
  }, [extractedPharmacyDetails]);

  // HIGH #3: Changed from auto-popup to opt-in
  // Previously auto-triggered — now shows a button instead
  const [hasExtractedPharmacy, setHasExtractedPharmacy] = useState(false);
  useEffect(() => {
    if (validPharmacyDetails.length > 0 && !hasShownPopup && !isConfirmed) {
      setHasExtractedPharmacy(true);
      setHasShownPopup(true);
      // No longer auto-shows popup — user clicks "Update your pharmacy" button
    }
  }, [validPharmacyDetails, hasShownPopup, isConfirmed]);

  // ✅ Initialize pharmacy from Formik stored values
  // For existing users with pre-selected pharmacy, auto-confirm to show the data
  useEffect(() => {
    if (!isInitialized) {
      let existingPharmacy = null;

      // Check for existing pharmacy data from previous submission
      if (values.pharmacydata?.selectedPharmacy) {
        existingPharmacy = values.pharmacydata.selectedPharmacy;
      } else if (values.medicationdata?.selectedPharmacy) {
        existingPharmacy = values.medicationdata.selectedPharmacy;
      }

      if (existingPharmacy && (existingPharmacy.name || existingPharmacy.pharmacy_name)) {
        // Normalize field names - DB uses pharmacy_name/shop_address, component uses name/address
        const normalized = {
          name: existingPharmacy.name || existingPharmacy.pharmacy_name || '',
          address: existingPharmacy.address || existingPharmacy.shop_address || '',
          city: existingPharmacy.city || '',
          state: existingPharmacy.state || '',
          zip: existingPharmacy.zip || '',
          phone: existingPharmacy.phone || '',
          fax: existingPharmacy.fax || '',
          pharmacy_name: existingPharmacy.pharmacy_name || existingPharmacy.name || '',
          shop_address: existingPharmacy.shop_address || existingPharmacy.address || '',
        };
        setPharmacyData(normalized);
        setIsConfirmed(true);
        setHasShownPopup(true);
      }

      setIsInitialized(true);
    }
  }, [isInitialized, values.pharmacydata, values.medicationdata]);

  // ✅ Format pharmacy details
  const formattedPharmacyData = useMemo(() => {
    const pharmacyFormatted =
      pharmacyData && pharmacyData.name
        ? `${pharmacyData.name}\n${pharmacyData.address || ""} ${pharmacyData.city || ""} ${pharmacyData.zip || ""}\n${pharmacyData.phone || ""}`
        : "";
    return { pharmacyFormatted, selectedPharmacy: pharmacyData || null };
  }, [pharmacyData]);

  // ✅ Only update Formik when user has confirmed or manually selected
  useEffect(() => {
    if (!isInitialized || !isConfirmed) return;

    setFieldValue("pharmacydata", {
      selectedPharmacy: pharmacyData,
      pharmacyFormatted: formattedPharmacyData.pharmacyFormatted,
    });

    setFieldValue("pharmacyformatted", formattedPharmacyData.pharmacyFormatted);

    if (values.medicationdata) {
      setFieldValue("medicationdata", {
        ...values.medicationdata,
        selectedPharmacy: pharmacyData,
      });
    }
  }, [formattedPharmacyData, isInitialized, isConfirmed, setFieldValue]);

  // ✅ Clean up blobs
  useEffect(() => {
    return () => {
      Object.values(blobUrls).forEach((url) => {
        if (url) URL.revokeObjectURL(url);
      });
    };
  }, [blobUrls]);

  if (!moduleData) return <p className="error">Page not found</p>;

  const fields = Array.isArray(moduleData) ? moduleData : [];

  // ✅ Popup handlers
  const handleConfirmYes = (selectedPharmacy) => {
    setPharmacyData(selectedPharmacy);
    setIsConfirmed(true);
    setShowConfirmPopup(false);
  };

  const handleConfirmNo = () => {
    setShowConfirmPopup(false);
  };

  return (
    <div className="medication-main-container">
      <div className="page-container">

        {/* HIGH #3: Opt-in button instead of auto-popup */}
        {hasExtractedPharmacy && !isConfirmed && !showConfirmPopup && (
          <div style={{ margin: '12px 0', textAlign: 'center' }}>
            <button
              type="button"
              onClick={() => setShowConfirmPopup(true)}
              style={{
                padding: '10px 20px',
                background: 'var(--color-primary, #088395)',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                fontWeight: 500,
                fontSize: '14px',
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              Update your pharmacy
            </button>
          </div>
        )}

        {/* HIGH #3: Pharmacy popup with dialog semantics + focus trap */}
        {showConfirmPopup && validPharmacyDetails.length > 0 && (
          <div
            className="popup-overlay"
            onClick={(e) => { if (e.target === e.currentTarget) handleConfirmNo(); }}
            onKeyDown={(e) => { if (e.key === 'Escape') handleConfirmNo(); }}
          >
            <div
              className="popup-content"
              role="dialog"
              aria-modal="true"
              aria-labelledby="pharmacy-popup-title"
              ref={(el) => { if (el) { const first = el.querySelector('button'); if (first) first.focus(); } }}
            >
              <h3 id="pharmacy-popup-title">Select your pharmacy</h3>
              <div className="popup-list">
                {validPharmacyDetails.map((ph, i) => (
                 <div key={i}>
                   <div className="popup-item">
                    <div>
                      <p><strong className="pharmacy-name">{ph.name}</strong></p>
                    <p>{ph.address}</p>
                    <p>{ph.city} {ph.zip}</p>
                    <p>{ph.phone}</p>
                    </div>
                    <div className="popup-actions">
                      <button className="yes-btn" onClick={() => handleConfirmYes(ph)}>Yes</button>
                    </div>
                  </div>
                    {i !== validPharmacyDetails.length - 1 && <hr />}
                 </div>
                ))}
              </div>
              <div style={{ marginTop: "10px" }}>
                <button className="no-btn" onClick={handleConfirmNo}>Cancel</button>
              </div>
            </div>
          </div>
        )}

        {/* ✅ PharmacyFinder */}
        {fields.map((item, index) => (
          <div key={index}>
            {item.type === "pharmacy" && (
              <PharmacyFinder
                key={`pharmacy-${index}`}
                csvData={item.flatCsvData || []}
                label={item.label}
                description={item.description}
                initialPharmacy={
                  isInitialized && pharmacyData
                    ? {
                        name: pharmacyData.name || "",
                        address: pharmacyData.address || "",
                        city: pharmacyData.city || "",
                        zip: pharmacyData.zip || "",
                        phone: pharmacyData.phone || "",
                      }
                    : null
                }
                onPharmacySelect={(pharmacy) => {
                  setPharmacyData(pharmacy);
                  setIsConfirmed(true);
                }}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default Pharmacy;